/**
 * Pure helpers for the Handheld updates page.
 *
 * Kept out of the component so the rules that actually carry risk -- which
 * runtimeVersion a channel is really on, and whether what the user typed
 * would strand a device -- can be read and tested without a DOM.
 */

export const CHANNELS = ["production", "beta"];

/**
 * The runtimeVersion a channel is really on.
 *
 * The APK is the authority: runtimeVersion ties a JS bundle to a NATIVE build,
 * so the native build's value is the one a bundle has to match. The JS entry is
 * only a fallback for a channel that has a bundle but no APK recorded yet.
 */
export function currentRuntimeVersion(channelState) {
  return (
    channelState?.apk?.runtimeVersion ||
    channelState?.js?.runtimeVersion ||
    ""
  );
}

/**
 * True when publishing `entered` to `channelState` would serve a bundle no
 * installed APK can run. Only meaningful once we know what the channel's APK
 * expects -- an unknown APK is not a mismatch, it is an unknown.
 */
export function isRuntimeMismatch(entered, channelState) {
  const expected = channelState?.apk?.runtimeVersion;
  if (!expected) return false;
  if (!entered) return false;
  return String(entered).trim() !== String(expected).trim();
}

/** updateIds are long uuids; the first 8 chars is what anyone actually reads. */
export function shortId(updateId) {
  if (!updateId) return "-";
  return String(updateId).slice(0, 8);
}

export function formatWhen(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

/**
 * Whatever the server said, in preference to anything we could invent.
 * A publish failure is the one moment the operator needs the real reason.
 */
export function serverError(error, fallback = "Request failed") {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "string" ? error.response.data : "") ||
    error?.message ||
    fallback
  );
}

export function isForbidden(error) {
  // Keyed on the API's own code, NOT on the 403 status. authJwt answers
  // "No token provided!" with a 403 too, and that means the session expired,
  // not that this account may not publish -- telling someone they lack
  // permission when they are simply logged out sends them hunting for an
  // admin over nothing.
  return error?.response?.data?.code === "RFID_PUBLISH_FORBIDDEN";
}
